import { Question } from "../types"
import { uploadDirect } from '@uploadcare/upload-client'

interface JotFormQuestion {
  qid: string
  text: string
  name: string
  description?: string
  options?: string
}

interface JotFormQuestionsResponse {
  responseCode: number
  message: string
  content: {
    [key: string]: JotFormQuestion
  }
}

interface JotFormSubmission {
  [qid: string]: string
}

const apiKey = process.env.JOTFORM_API_KEY || ""

export async function fetchJotFormQuestions(
  formId: string
): Promise<Question[]> {
  try {
    if (!formId || !apiKey) {
      throw new Error(
        "JotForm environment variables are not set (JOTFORM_FORGE_FORM_ID or JOTFORM_API_KEY)"
      )
    }

    const response = await fetch(
      `https://api.jotform.com/form/${formId}/questions?apiKey=${apiKey}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data: JotFormQuestionsResponse = await response.json()

    if (data.responseCode !== 200) {
      throw new Error(`API error: ${data.message}`)
    }

    const questions: Question[] = Object.values(data.content).map(
      (question) => {
        return {
          text: question.text,
          subtext: question.description,
        }
      }
    )

    return questions
  } catch (error) {
    console.error("Error fetching JotForm questions:", error)
    return []
  }
}

export async function submitJotFormAnswers(
  formId: string,
  answers: string[]
): Promise<void> {
  try {
    if (!formId || !apiKey) {
      throw new Error(
        "JotForm environment variables are not set (JOTFORM_FORM_ID or JOTFORM_API_KEY)"
      )
    }
    const response = await fetch(
      `https://api.jotform.com/form/${formId}/questions?apiKey=${apiKey}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const questionsData: JotFormQuestionsResponse = await response.json()

    if (questionsData.responseCode !== 200) {
      throw new Error(`API error: ${questionsData.message}`)
    }

    const submission: JotFormSubmission = {}

    Object.values(questionsData.content).forEach((question, index) => {
      if (answers[index]) {
        submission[question.qid] = answers[index] || ""
      }
    })

    const submitResponse = await fetch(
      `https://api.jotform.com/form/${formId}/submissions?apiKey=${apiKey}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify([submission]),
      }
    )

    if (!submitResponse.ok) {
      throw new Error(`HTTP error! status: ${submitResponse.status}`)
    }

    const submitData = await submitResponse.json()

    if (submitData.responseCode !== 200) {
      throw new Error(`API error: ${submitData.message}`)
    }
  } catch (error) {
    console.error("Error submitting JotForm answers:", error)
    throw error
  }
}

export async function fetchContactFormOptions(
  formId: string
): Promise<{ id: string; title: string }[]> {
  try {
    if (!formId || !apiKey) {
      throw new Error(
        "JotForm environment variables are not set (JOTFORM_CONTACT_FORM_ID or JOTFORM_API_KEY)"
      )
    }

    const response = await fetch(
      `https://api.jotform.com/form/${formId}/questions?apiKey=${apiKey}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data: JotFormQuestionsResponse = await response.json()

    if (data.responseCode !== 200) {
      throw new Error(`API error: ${data.message}`)
    }

    const moduleOptions: { id: string; title: string }[] = []

    Object.entries(data.content).forEach(([qid, question]) => {
      const questionName = question.name.toLowerCase()
      if (questionName === "module") {
        if (question.options) {
          const options = question.options
            .split("|")
            .map((option) => option.trim())
          options.forEach((option) => {
            moduleOptions.push({
              id: option,
              title: option,
            })
          })
        }
      }
    })
    return moduleOptions
  } catch (error) {
    console.error("Error fetching contact form options:", error)
    return []
  }
}


const uploadcarePublicKey = process.env.UPLOADCARE_PUBLIC_KEY || "";

async function uploadFileToUploadcare(file: File): Promise<string> {
  if (!uploadcarePublicKey) {
    throw new Error("Uploadcare Public Key is not set.");
  }

  const arrayBuffer = await file.arrayBuffer();
  const buffer = Buffer.from(arrayBuffer);

  const result = await uploadDirect(
    buffer,
    {
      publicKey: uploadcarePublicKey,
      store: 'auto',
      fileName: file.name,
      contentType: file.type,
    }
  );

  return result.cdnUrl;
}

export async function submitContactForm(
  formId: string,
  data: {
    module: string
    email: string
    message: string
    files?: File[]
  }
): Promise<void> {
  try {
    if (!formId || !apiKey) {
      throw new Error(
        "JotForm environment variables are not set (JOTFORM_CONTACT_FORM_ID or JOTFORM_API_KEY)"
      )
    }

    const questionsResponse = await fetch(
      `https://api.jotform.com/form/${formId}/questions?apiKey=${apiKey}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )

    if (!questionsResponse.ok) {
      throw new Error(`HTTP error! status: ${questionsResponse.status}`)
    }

    const questionsData: JotFormQuestionsResponse =
      await questionsResponse.json()

    if (questionsData.responseCode !== 200) {
      throw new Error(`API error: ${questionsData.message}`)
    }

    const submission: JotFormSubmission = {}

    const fieldMapping: { [key: string]: string } = {};

    Object.entries(questionsData.content).forEach(([qid, question]) => {
      const questionName = question.name.toLowerCase();
      fieldMapping[questionName] = qid;
    });

    const submissionData: { [key: string]: string | string[] } = {};

    if (fieldMapping.module) {
      submissionData[`submission[${fieldMapping.module}]`] = data.module;
    }
    if (fieldMapping.emailaddress) {
      submissionData[`submission[${fieldMapping.emailaddress}]`] = data.email;
    }
    if (fieldMapping.sendmessage) {
      submissionData[`submission[${fieldMapping.sendmessage}]`] = data.message;
    }

    if (data.files && data.files.length > 0) {
      const uploadcareUrls: string[] = [];
      for (const file of data.files) {
        const url = await uploadFileToUploadcare(file);
        uploadcareUrls.push(url);
      }

      const uploadcareFieldId = fieldMapping.uploadfileimg;

      if (uploadcareFieldId) {
        submissionData[`submission[${uploadcareFieldId}]`] = uploadcareUrls.join("\n");
      } else {
        throw new Error("Uploadcare field 'uploadfileimg' not found in form questions.");
      }
    }

    const formBody = new URLSearchParams();
    for (const key in submissionData) {
      formBody.append(key, String(submissionData[key]));
    }

    const submitResponse = await fetch(
      `https://api.jotform.com/form/${formId}/submissions?apiKey=${apiKey}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody.toString(),
      }
    );

    if (!submitResponse.ok) {
      const errorText = await submitResponse.text();
      throw new Error(`HTTP error! status: ${submitResponse.status}, message: ${errorText}`);
    }

    const submitData = await submitResponse.json();

    if (submitData.responseCode !== 200) {
      throw new Error(`API error: ${submitData.message}`);
    }
  } catch (error) {
    console.error("Error submitting contact form:", error);
    throw error;
  }
}
